import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Tooltip,
} from "@mui/material";
import { DeleteOutline, InfoOutlined, ContentCopy } from "@mui/icons-material";

import coreUtils from "../../utils/coreUtils";
import "../../styles/components/Common/KeyValueTable.scss";
import { Replace } from "lucide-react";

const rawItem = {
  id: Date.now().toString(),
  isNew: true,
  isEdit: false,
  var_name: "",
  var_value: "",
  is_dynamic: false
};

const inputProps = {
  classes: { notchedOutline: "no-border" },
  className: "custom-textfield",
};

const KeyValueTable = function ({
  allItems = [],
  onCreateItem,
  onUpdateItem,
  onGetNewItem,
  onDeleteItem,
  onValidate,
  isReadOnly = false,
  itemName = "item",
  sxTableContainer = {},
  sxCell = {},
  json = false,
  handleReplaceValuesWithVarName,
  enableCopyPath = false,
  showSerialNumber = true
}) {
  const dispatch = useDispatch();
  const [items, setItems] = useState(allItems);
  const [mutableItems, setMutableItems] = useState({});
  const [errorMsg, setErrorMsg] = useState("");
  const [newItem, setNewItem] = useState(
    onGetNewItem ? onGetNewItem() : { ...rawItem }
  );
  const [editingItemId, setEditingItemId] = useState("");
  const [deletingItemId, setDeletingItemId] = useState("");

  const cellStyle = { textAlign: "center", padding: "3px", margin: 0, ...sxCell, color: "var(--text-primary)" };
  const centerAlignCellStyle = { textAlign: "center", padding: 0, margin: 0, ...sxCell, color: "var(--text-primary)" };
  const textFieldStyle = {
    height: "27px",
    "& .MuiInputBase-input": { padding: "5px 10px", fontSize: "12px", ...sxCell },
  };

  useEffect(() => {
    setItems(allItems);
    setMutableItems({});
    setEditingItemId("");
    setDeletingItemId("");
  }, [allItems]);

  useEffect(() => {
    if (!isReadOnly) {
      updateMutableItems();
      document.addEventListener("mousedown", handleOutsideClick);
      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    }
  }, [allItems, editingItemId])

  const handleOutsideClick = () => {
    updateMutableItems();
    setEditingItemId("");
  };

  const updateMutableItems = () => {
    setMutableItems(
      items.reduce(
        (prev, curr) => ({
          ...prev,
          [curr.id]: { ...curr },
        }),
        {}
      )
    );
  }

  const getNewItem = () => {
    if (onGetNewItem) return onGetNewItem();
    return { ...rawItem };
  };

  const validateItem = useCallback(
    (var_name, var_value, isNew) => {
      if (isNew && coreUtils.isStringInvalidOrBlank(var_name)) {
        setErrorMsg("Please enter a valid key");
        return false;
      }
      if (coreUtils.isStringInvalidOrBlank(var_value)) {
        setErrorMsg("Please enter a valid value");
        return false;
      }
      if (isNew) {
        const matchingKey = items.find((item) => item.var_name === var_name);
        if (matchingKey) {
          setErrorMsg(`Key name collides with other ${itemName} key`);
          return false;
        }
      }

      return true;
    },
    [items]
  );

  const createItem = useCallback(
    (item) => {
      if (!validateItem(item.var_name, item.var_value, true)) return false;
      const data = {
        var_name: item.var_name,
        var_value: item.var_value,
      };
      onCreateItem(data);
      setNewItem(getNewItem());
    },
    [validateItem]
  );

  const saveNewItem = useCallback(
    (item) => {
      if (item.var_name && item.var_value) createItem(item);
    },
    [createItem]
  );

  const updateItemValue = useCallback(
    (id) => {
      const updatedValue = mutableItems[id]?.var_value || "";
      if (!validateItem(mutableItems[id]?.var_name, updatedValue)) return false;
      if (updatedValue) {
        onUpdateItem({
          var_value: updatedValue,
          id: id,
        });
      }
    },
    [mutableItems, setItems]
  );

  const handleNewItemChange = useCallback(
    (var_name, var_value) => {
      setNewItem((prev) => ({ ...prev, [var_name]: var_value }));
      setErrorMsg("");
    },
    [setNewItem, setErrorMsg]
  );

  const handleChange = (e, id) => {
    setMutableItems((prev) => ({
      ...prev,
      [id]: { ...prev[id], var_value: e.target.value },
    }));
  };

  const deleteItem = async () => {
    try {
      await onDeleteItem(deletingItemId);
      setDeletingItemId(""); // Clear the deleting state
      // Update local items state to reflect deletion
      setItems(prevItems => prevItems.filter(item => item.id !== deletingItemId));
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleKeyDown = (e, item, isNew) => {
    if (e.key === "Enter") {
      if (isNew) {
        saveNewItem(item);
      } else {
        updateItemValue(item);
      }
    }
  };

  const renderDeleteDialog = () => {
    return (
      <Dialog
        maxWidth="xs"
        onClose={() => setDeletingItemId("")}
        open={Boolean(deletingItemId)}
      >
        <DialogTitle>Delete {itemName}</DialogTitle>
        <div style={{ minWidth: "300px", padding: "0px 18px 14px 18px" }}>
          Are you sure you want to delete this {itemName}? This cannot be
          undone.
        </div>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeletingItemId("")}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={() => deleteItem()}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderNewItem = () => {
    return (
      <TableRow>
        <TableCell sx={cellStyle}></TableCell>
        <TableCell sx={centerAlignCellStyle}>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            placeholder="Key"
            value={newItem.var_name}
            onChange={(e) => handleNewItemChange("var_name", e.target.value)}
            onKeyDown={(e) => {
              handleKeyDown(e, newItem, true);
            }}
            InputProps={inputProps}
            sx={textFieldStyle}
          />
        </TableCell>
        <TableCell sx={centerAlignCellStyle}>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            placeholder="Value"
            value={newItem.var_value}
            onChange={(e) => handleNewItemChange("var_value", e.target.value)}
            onKeyDown={(e) => {
              handleKeyDown(e, newItem, true);
            }}
            InputProps={inputProps}
            sx={textFieldStyle}
          />
        </TableCell>
        <TableCell sx={centerAlignCellStyle}></TableCell>
        {
          handleReplaceValuesWithVarName &&
          <TableCell sx={centerAlignCellStyle}></TableCell>
        }
      </TableRow>
    );
  };

  const getJsonFormatted = (value) => {
    try {
      if (!value) {
        return JSON.stringify({});
      } else if (typeof value === "string") {
        const jsonParsed = JSON.parse(value);
        return JSON.stringify(jsonParsed, null, 2);
      } else {
        return JSON.stringify(value, null, 2);
      }
    } catch (e) {
      return value;
    }
  }

  const generateKeyPath = (var_name, var_value, nestedPath = '') => {
    if (!json || !var_value) return var_name;

    try {
      const parsedValue = typeof var_value === 'string' ? JSON.parse(var_value) : var_value;

      if (typeof parsedValue === 'object' && parsedValue !== null) {
        if (nestedPath) {
          // Split by dot and wrap each segment in ["segment"]
          const segments = nestedPath.split('.').filter(Boolean);
          const formatted = segments.map(segment => `["${segment}"]`).join('');
          return `${var_name}${formatted}`;
        }
        return var_name;
      }

      return var_name;
    } catch (e) {
      return var_name;
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleCopyPath = (var_name, var_value, nestedPath = '') => {
    const finalKeyPath = getFinalKeyPath(var_name, var_value, nestedPath);
    copyToClipboard(finalKeyPath);
  };

  const getFinalKeyPath = (var_name, var_value, nestedPath = '') => {
    let baseVar = var_name;
    let fullNestedPath = nestedPath;

    if (var_name.includes('.')) {
      const segments = var_name.split('.');
      baseVar = segments[0];
      const prefixPath = segments.slice(1).join('.');
      fullNestedPath = prefixPath ? `${prefixPath}${nestedPath}` : nestedPath;
    }

    const keyPath = generateKeyPath(baseVar, var_value, fullNestedPath);
    return `{{${keyPath}}}`;
  };

  const renderJsonWithCopyButtons = (var_name, var_value) => {
    try {
      const parsedValue = typeof var_value === 'string' ? JSON.parse(var_value) : var_value;
      if (typeof parsedValue === 'object' && parsedValue !== null) {
        return (
          <div style={{ fontFamily: 'monospace', fontSize: '12px', lineHeight: '1.4' }}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                const copyIcon = e.currentTarget.querySelector('.copy-icon');
                if (copyIcon) copyIcon.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                const copyIcon = e.currentTarget.querySelector('.copy-icon');
                if (copyIcon) copyIcon.style.opacity = '0';
              }}
            >
              <span>{'{'}</span>
              <IconButton
                size="small"
                onClick={() => handleCopyPath(var_name, var_value)}
                title={`${var_name}`}
                className="copy-icon"
                sx={{
                  padding: '2px',
                  minWidth: '20px',
                  height: '20px',
                  opacity: 0,
                  transition: 'opacity 0.2s'
                }}
              >
                <ContentCopy sx={{ fontSize: '12px' }} />
              </IconButton>
            </span>
            {renderNestedJson(parsedValue, var_name, 1)}
            <span>{'}'}</span>
          </div>
        );
      }
      return <pre>{JSON.stringify(parsedValue, null, 2)}</pre>;
    } catch (e) {
      return <pre>{var_value}</pre>;
    }
  };

  const renderNestedJson = (obj, var_name, indent = 0, basePath = "") => {
    const elements = [];

    for (const [key, value] of Object.entries(obj)) {
      const currentPath = basePath ? `${basePath}.${key}` : key;
      const isObject = typeof value === "object" && value !== null && !Array.isArray(value);
      const isArray = Array.isArray(value);

      if (isObject) {
        elements.push(
          <div key={currentPath} style={{ marginLeft: `${indent * 20}px` }}>
            <span
              style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
              onMouseEnter={(e) => {
                const copyIcon = e.currentTarget.querySelector('.copy-icon');
                if (copyIcon) copyIcon.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                const copyIcon = e.currentTarget.querySelector('.copy-icon');
                if (copyIcon) copyIcon.style.opacity = '0';
              }}
            >
              <span style={{ color: "#0066cc" }}>"{key}"</span>
              <span>: {'{'}</span>
              <IconButton
                size="small"
                onClick={() => handleCopyPath(var_name, obj, currentPath)}
                title={getFinalKeyPath(var_name, obj, currentPath)}
                className="copy-icon"
                sx={{
                  padding: "2px",
                  minWidth: "20px",
                  height: "20px",
                  opacity: 0,
                  transition: "opacity 0.2s"
                }}
              >
                <ContentCopy sx={{ fontSize: "12px" }} />
              </IconButton>
            </span>
            {renderNestedJson(value, var_name, indent + 1, currentPath)}
            <span style={{ marginLeft: `${indent * 20}px` }}>{'}'}</span>
          </div>
        );
      } else if (isArray) {
        elements.push(
          <div key={currentPath} style={{ marginLeft: `${indent * 20}px` }}>
            <span
              style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
              onMouseEnter={(e) => {
                const copyIcon = e.currentTarget.querySelector('.copy-icon');
                if (copyIcon) copyIcon.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                const copyIcon = e.currentTarget.querySelector('.copy-icon');
                if (copyIcon) copyIcon.style.opacity = '0';
              }}
            >
              <span style={{ color: "#0066cc" }}>"{key}"</span>
              <span>: [</span>
              <IconButton
                size="small"
                onClick={() => handleCopyPath(var_name, obj, currentPath)}
                title={getFinalKeyPath(var_name, obj, currentPath)}
                className="copy-icon"
                sx={{
                  padding: "2px",
                  minWidth: "20px",
                  height: "20px",
                  opacity: 0,
                  transition: "opacity 0.2s"
                }}
              >
                <ContentCopy sx={{ fontSize: "12px" }} />
              </IconButton>
            </span>
            <div style={{ marginLeft: `${(indent + 1) * 20}px` }}>
              {value.map((item, index) => {
                const itemPath = `${currentPath}.${index}`;
                if (typeof item === 'object' && item !== null) {
                  return (
                    <div key={itemPath}>
                      <span
                        style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
                        onMouseEnter={(e) => {
                          const copyIcon = e.currentTarget.querySelector('.copy-icon');
                          if (copyIcon) copyIcon.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                          const copyIcon = e.currentTarget.querySelector('.copy-icon');
                          if (copyIcon) copyIcon.style.opacity = '0';
                        }}
                      >
                        <span>{'{'}</span>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyPath(var_name, obj, itemPath)}
                          title={getFinalKeyPath(var_name, obj, itemPath)}
                          className="copy-icon"
                          sx={{
                            padding: "2px",
                            minWidth: "20px",
                            height: "20px",
                            opacity: 0,
                            transition: "opacity 0.2s"
                          }}
                        >
                          <ContentCopy sx={{ fontSize: "12px" }} />
                        </IconButton>
                      </span>
                      {renderNestedJson(item, var_name, indent + 2, itemPath)}
                      <span>{'}'}</span>
                    </div>
                  );
                } else {
                  return (
                    <div key={itemPath}
                      style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}
                      onMouseEnter={(e) => {
                        const copyIcon = e.currentTarget.querySelector('.copy-icon');
                        if (copyIcon) copyIcon.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        const copyIcon = e.currentTarget.querySelector('.copy-icon');
                        if (copyIcon) copyIcon.style.opacity = '0';
                      }}
                    >
                      <span>{JSON.stringify(item)}</span>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyPath(var_name, obj, itemPath)}
                        title={getFinalKeyPath(var_name, obj, itemPath)}
                        className="copy-icon"
                        sx={{
                          padding: "2px",
                          minWidth: "20px",
                          height: "20px",
                          opacity: 0,
                          transition: "opacity 0.2s"
                        }}
                      >
                        <ContentCopy sx={{ fontSize: "12px" }} />
                      </IconButton>
                    </div>
                  );
                }
              })}
            </div>
            <span style={{ marginLeft: `${indent * 20}px` }}>]</span>
          </div>
        );
      } else {
        elements.push(
          <div key={currentPath} style={{ marginLeft: `${indent * 20}px` }}>
            <span
              style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer" }}
              onMouseEnter={(e) => {
                const copyIcon = e.currentTarget.querySelector('.copy-icon');
                if (copyIcon) copyIcon.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                const copyIcon = e.currentTarget.querySelector('.copy-icon');
                if (copyIcon) copyIcon.style.opacity = '0';
              }}
            >
              <div>
                <span style={{ color: "#0066cc" }}>"{key}":</span>
                <span style={{ whiteSpace: "pre-wrap", fontFamily: "monospace", wordBreak: "break-all" }}>{JSON.stringify(value)}</span>
              </div>
              <IconButton
                size="small"
                onClick={() => handleCopyPath(var_name, obj, currentPath)}
                title={getFinalKeyPath(var_name, obj, currentPath)}
                className="copy-icon"
                sx={{
                  padding: "2px",
                  minWidth: "20px",
                  height: "20px",
                  opacity: 0,
                  transition: "opacity 0.2s",
                  marginTop: "2px",
                  flexShrink: 0
                }}
              >
                <ContentCopy sx={{ fontSize: "12px" }} />
              </IconButton>
            </span>
          </div>
        );
      }
    }

    return <div>{elements}</div>;
  };


  if (items.length === 0 && isReadOnly) {
    return <div>No {itemName}s found!</div>
  }

  return (
    <Box sx={{ py: 2, width: "100%" }} className="keyValueTable">
      <TableContainer sx={{ backgroundColor: "var(--background-primary)", ...sxTableContainer }}>
        <Table size="small" aria-label="simple" sx={{ border: '1px solid var(--borderColor)', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ alignItems: "center", height: "1vh" }}>
              {showSerialNumber && (
                <TableCell sx={{ ...cellStyle, width: "10%" }}>S. No.</TableCell>
              )}
              <TableCell sx={{ ...cellStyle, width: "30%" }}>Key</TableCell>
              <TableCell sx={{ ...cellStyle, width: "50%" }}>Value</TableCell>

              {!isReadOnly && (
                <TableCell sx={{ width: "5%", ...cellStyle }}></TableCell>
              )}
              {handleReplaceValuesWithVarName && (
                <TableCell sx={{ width: "5%", ...cellStyle }}></TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(({ id, var_name, var_value, is_dynamic }, index) => (
              <TableRow key={id}>
                {showSerialNumber && (
                  <TableCell sx={{ ...cellStyle, width: "10%" }}>{index + 1}</TableCell>
                )}
                <TableCell
                  sx={{ textAlign: "left", margin: 0, padding: "5px 10px", width: "30%", ...sxCell, wordBreak: "break-word" }}
                  className="disabled"
                >
                  {var_name}
                </TableCell>
                {!isReadOnly && (
                  <TableCell sx={centerAlignCellStyle}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      size="small"
                      placeholder="Value"
                      disabled={is_dynamic}
                      onClick={() => setEditingItemId(id)}
                      value={
                        editingItemId === id ? mutableItems[id]?.var_value : var_value
                      }
                      onChange={(e) => {
                        handleChange(e, id);
                      }}
                      onKeyDown={(e) => {
                        handleKeyDown(e, id);
                      }}
                      InputProps={inputProps}
                      sx={textFieldStyle}
                    />
                  </TableCell>
                )}
                {isReadOnly && (
                  <TableCell
                    sx={{
                      textAlign: "left",
                      margin: 0,
                      padding: "5px 10px",
                      width: "50%",
                      maxWidth: "0", // This forces the cell to respect content width
                      whiteSpace: "pre-wrap",
                      fontFamily: "monospace",
                      fontSize: "12px",
                      lineHeight: "1.4",
                      ...sxCell
                    }}
                  >
                    {json && enableCopyPath ? renderJsonWithCopyButtons(var_name, var_value) : (json ? <pre>{getJsonFormatted(var_value)}</pre> : var_value)}
                  </TableCell>
                )}

                {handleReplaceValuesWithVarName && (
                  <TableCell sx={centerAlignCellStyle}>
                    {!is_dynamic && (
                      <IconButton
                        size="small"
                        aria-label="Replace Values"
                        onClick={() => handleReplaceValuesWithVarName(id)}
                        title="Replace Values in your data"
                      >
                        <Replace />
                      </IconButton>
                    )}
                  </TableCell>
                )}
                {!isReadOnly && (
                  <TableCell sx={centerAlignCellStyle}>
                    {!is_dynamic ? (
                      <IconButton
                        size="small"
                        aria-label="Delete item"
                        onClick={() => setDeletingItemId(id)}
                        title="Delete Variable"
                      >
                        <DeleteOutline />
                      </IconButton>
                    ) : (
                      <Tooltip componentsProps={{
                        tooltip: {
                          sx: {
                            backgroundColor: 'var(--background-secondary)', // e.g., slate-800
                            borderRadius: '8px',
                            padding: '8px 12px',
                          },
                        }
                      }} title={
                        <span className="text-sm">
                          This is a Post Script Variable, and its value is generated from the response of the entity where it has been added.
                        </span>
                      }>
                        <IconButton
                          size="small"
                          aria-label="Info Button"
                          title="Info Button"
                        >
                          <InfoOutlined />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!isReadOnly && renderNewItem()}
          </TableBody>
        </Table>
      </TableContainer>
      {errorMsg && <p style={{ color: "#bb0101", marginTop: "8px" }}>{errorMsg}</p>}
      {!isReadOnly && renderDeleteDialog()}
    </Box>
  );
};

export default KeyValueTable;
